import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import moment, { duration } from 'moment'
import { useNavigate, useParams } from 'react-router-dom'

import {
  editCompany as editCompanyRequest,
  getCompanies,
  getCompanyDetails,
  importFromGus,
} from 'app/actions/company'
import { getUserInfo, removeUserFromCompany } from 'app/actions/user'
import { CompanyType } from 'app/constants/companyType'
import { SERVICE_TIME_FORMAT, TIME_FORMAT } from 'app/constants/date'
import { ROUTES } from 'app/constants/routes'
import {
  CompanyDetailsModel,
  ImportTypes,
} from 'app/models/companyDetailsModel'
import { RolesAssignModel } from 'app/models/companyDetailsModel'
import { RolePermissionModel } from 'app/models/rolePermissionModel'
import { UserModel } from 'app/models/userModel'
import { formatExtendedDuration } from 'app/common/dateHelper'
import { validateEmail, validateNip } from 'app/common/validationHelper'
import { isAdmin, isPaymentAdmin, saveUserInfo } from 'app/services/local/roleService'
import { getCompanyTypeIcon } from 'app/services/company/companyIconResolver'
import { getCompanyTypeName } from 'app/common/converters/companyTypeToTextConverter'
import { ROLES_KEY, fetchRoles } from 'app/containers/administration/roleQueries'
import { GROUPS_KEY, fetchGroups } from 'app/containers/administration/groupQueries'
import { companyUsersKey, fetchCompanyUsers, fetchUsers, usersKey } from 'app/containers/administration/userQueries'

import { TableProvider } from '@/modules/drivers/context/TableContext'
import {
  AdminDateField,
  AdminIcon,
  CheckboxField,
  SubNavigationBar,
  SubNavigationButton,
} from '@/modules/shared/components'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { Dropdown } from '@/app/features/company-selection/Dropdown'
import { toast } from '@/lib/toasts/api'
import { Button } from '@/ui/button'
import { TextField } from '@/ui/form/controls/TextField'
import { CompanyUsersTab, rolesOf } from './components/CompanyUsersTab'
import { SplitServiceCompanyModal } from '@/app/components/administration/SplitServiceCompanyModal'
import { IpAllowListEditor } from '@/modules/shared/components/ipAllowList/IpAllowListEditor'

const TABS = { Company: 'company', User: 'user', IpAccess: 'ipAccess' }

/** Pola dat, które serwer chce w swoim formacie, nie w ISO od momenta. */
const DATE_FIELDS = [
  'subscriptionExpirationDate',
  'suspensionDate',
  'suspendedFrom',
  'suspendedTo',
  'newPackageFrom',
  'newPackageTo',
  'newSubscriptionExpirationDate',
  'activationDate',
  'sellDate',
  'paymentDate',
  'paymentCreationDate',
  'longPaymentDate',
  'bonusStartedAt',
  'currentDriversModificationDate',
] as const

const DEFAULT_PAYMENT_REMINDER_DURATION = 7

const isEmpty = (value?: string) => value == null || String(value).trim() === ''

type Validation = Record<string, string | null> & { isValid: boolean }

/** Reguły przepisane z `getValidationRules()` - część obowiązuje tylko firmy księgowe. */
const validate = (
  company: CompanyDetailsModel,
  selectedGroups: string[],
  t: (key: string) => string,
): Validation => {
  const result: Validation = { isValid: true } as Validation
  const fail = (field: string, message: string) => {
    result[field] = t(message)
    result.isValid = false
  }

  if (isEmpty(company.companyName)) {
    fail('companyName', 'administrationPanel-companies.validation-companyName')
  }
  if (isEmpty(company.uniqueIdentifier)) {
    fail(
      'uniqueIdentifier',
      'administrationPanel-companies.validation-uniqueIdentifier',
    )
  }
  if (!isEmpty(company.nip) && !validateNip(company.nip)) {
    fail('nip', 'administrationPanel-companies.validation-nip-invalid')
  }
  for (const field of ['email', 'notificationsEmail', 'contactEmail'] as const) {
    if (!isEmpty(company[field]) && !validateEmail(company[field])) {
      fail(field, 'administrationPanel-companies.validation-email-invalid')
    }
  }
  if (
    !isEmpty(company.contactCCEmail) &&
    !company.contactCCEmail
      .split(/[;,]/)
      .every((item) => isEmpty(item) || validateEmail(item.trim()))
  ) {
    fail(
      'contactCCEmail',
      'administrationPanel-companies.validation-email-invalid',
    )
  }

  // Firma księgowa dodatkowo musi mieć NIP, telefon, e-mail kontaktowy i grupę.
  if (company.accountingOnly) {
    if (isEmpty(company.nip)) {
      fail('nip', 'administrationPanel-companies.nip-empty')
    }
    if (isEmpty(company.contactPhone)) {
      fail('contactPhone', 'administrationPanel-companies.contact-phone-empty')
    }
    if (isEmpty(company.contactEmail)) {
      fail('contactEmail', 'administrationPanel-companies.contact-email-empty')
    }
    if (selectedGroups == null || selectedGroups.length === 0) {
      fail('selectedGroups', 'administrationPanel-companies.group-list-empty')
    }
  }

  return result
}

const AdminEditCompanyPageContent: React.FC = () => {
  const { t } = useLocalize()
  const navigate = useNavigate()
  const { companyId } = useParams<{ companyId: string }>()

  const [company, setCompany] = React.useState<CompanyDetailsModel | null>(null)
  const [users, setUsers] = React.useState<UserModel[]>([])
  const [selectedGroups, setSelectedGroups] = React.useState<string[]>([])
  const [activeTab, setActiveTab] = React.useState(TABS.Company)
  const [canSave, setCanSave] = React.useState(false)
  const [submitted, setSubmitted] = React.useState(false)
  const [noteTouched, setNoteTouched] = React.useState(false)
  const [convertOpen, setConvertOpen] = React.useState(false)

  const companyQuery = useQuery({
    queryKey: ['admin', 'editCompany', companyId],
    queryFn: () => getCompanyDetails(companyId, true),
  })
  const { data: groups = [] } = useQuery({
    queryKey: GROUPS_KEY,
    queryFn: () => fetchGroups(),
  })
  const { data: roles = [] } = useQuery({
    queryKey: ROLES_KEY,
    queryFn: fetchRoles,
  })

  const loaded = companyQuery.data
  React.useEffect(() => {
    if (!loaded) return
    setCompany(loaded)
    setSelectedGroups(loaded.groupList ?? [])
  }, [loaded])

  // Użytkownicy firmy: pełna lista z tenanta przefiltrowana do tych, którzy mają
  // rolę w tej firmie albo należą do niej bez ról.
  const companyUsersQuery = useQuery({
    queryKey: companyUsersKey(loaded?.id ?? null, loaded?.tenantId ?? null),
    queryFn: () => fetchCompanyUsers(loaded.id),
    enabled: loaded != null && !(!isAdmin() && isPaymentAdmin()),
  })

  React.useEffect(() => {
    const list = companyUsersQuery.data
    if (!list) return
    setUsers(
      list.filter(
        (user) =>
          (user.roles.length === 0 &&
            String(user.companyId) === String(companyId)) ||
          user.roles.some(
            (role) => String(role.company.id) === String(companyId),
          ),
      ),
    )
  }, [companyUsersQuery.data, companyId])

  const availableUsersScope = isAdmin()
    ? null
    : (loaded?.tenant?.id ?? loaded?.id ?? null)
  const { data: availableUsers = [] } = useQuery({
    queryKey: usersKey(availableUsersScope),
    queryFn: () => fetchUsers(availableUsersScope ?? undefined),
    enabled: !isPaymentAdmin(),
  })

  if (!company) return null

  const validation = validate(company, selectedGroups, t)
  const error = (field: string) => (submitted ? (validation[field] ?? null) : null)

  const patch = (changes: Partial<CompanyDetailsModel>) => {
    setCompany((prev) => ({ ...prev, ...changes }))
    setCanSave(true)
  }

  const backUrl = company.tenant
    ? ROUTES.AdministrationPanel.CompanyTab.replace(
        ':companyId',
        company.tenant.id,
      ).replace(':tab', 'company')
    : ROUTES.AdministrationPanel.NoCompanyTab.replace(':tab', 'company')

  const fillFromGus = async () => {
    if (isEmpty(company.nip) || !validateNip(company.nip)) {
      setSubmitted(true)
      return
    }
    const result = await importFromGus(company.nip)
    if (!result) return

    if (result.name) {
      toast.success({
        title: t('administrationPanel-companies.imported-from-gus'),
        text: result.name,
      })
      patch({
        nip: result.nip,
        regon: result.regon,
        krs: result.krs,
        buildingNumber: result.buildingNumber,
        houseNumber: result.flatNumber,
        companyName: result.name,
        postalCode: result.postalCode,
        city: result.city,
        street: result.street,
        contactEmail: result.email,
        contactPhone: result.phone,
      })
    } else {
      toast.warning({
        title: t('administrationPanel-companies.not-imported-from-gus'),
        text: t('administrationPanel-companies.not-imported-from-gus-details'),
      })
    }
  }

  const save = async () => {
    setSubmitted(true)
    if (!validate(company, selectedGroups, t).isValid) return

    const userRoles: RolesAssignModel[] = users.map((user) => ({
      userId: user.id,
      roles: rolesOf(user, companyId).map((role) => ({ id: role.id })),
    }))

    const payload: any = { ...company, userRoles, groupList: selectedGroups }
    for (const field of DATE_FIELDS) {
      payload[field] =
        company[field] != null
          ? moment(company[field]).format(SERVICE_TIME_FORMAT)
          : null
    }
    payload.paymentReminderDate = company.enablePaymentReminder
      ? company.paymentReminderDate
      : null
    payload.paymentReminderDuration = company.enablePaymentReminder
      ? company.paymentReminderDuration > 0
        ? company.paymentReminderDuration
        : DEFAULT_PAYMENT_REMINDER_DURATION
      : 0

    const saved = await editCompanyRequest(payload)

    if (!isAdmin()) {
      saveUserInfo(await getUserInfo(), await fetchRoles(), await getCompanies())
    }

    if (saved.error == null) {
      toast.success({
        title: t('administrationPanel-companies.company-edited'),
        text: company.companyName,
      })
      setCanSave(false)
      await companyQuery.refetch()
      await companyUsersQuery.refetch()
    } else {
      toast.error({
        title: t('administrationPanel-companies.company-not-edited'),
        text: t(`errorCode.${saved.error}`),
      })
    }
  }

  const servers =
    company.serverNames != null && company.serverNames.length > 0
      ? company.serverNames
          .filter((name, index, all) => all.indexOf(name) === index)
          .join(', ')
      : '-'

  const isService = company.type === CompanyType.SERVICE
  const showServerInfo =
    (isAdmin() || company.tenant != null) &&
    company.type === CompanyType.NORMAL &&
    !company.accountingOnly

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-content-primary">
      <div className="tw-flex-shrink-0 tw-px-4 tw-pt-2">
        <Button
          variant="primary"
          mode="outline"
          size="sm"
          onClick={() => navigate(backUrl)}
        >
          <AdminIcon name="arrow-left" className="tw-h-4 tw-w-4" />
          {t('administrationPanel-companies.go-back')}
        </Button>
      </div>

      <SubNavigationBar>
        <SubNavigationButton
          isActive={activeTab === TABS.Company}
          hasError={submitted && !validation.isValid}
          onClick={() => setActiveTab(TABS.Company)}
        >
          {t('administrationPanel-companies.edit-company')}
        </SubNavigationButton>
        {!isPaymentAdmin() && (
          <SubNavigationButton
            isActive={activeTab === TABS.User}
            onClick={() => setActiveTab(TABS.User)}
          >
            {t('administrationPanel.users')}
          </SubNavigationButton>
        )}
        {isAdmin() && (
          <SubNavigationButton
            isActive={activeTab === TABS.IpAccess}
            onClick={() => setActiveTab(TABS.IpAccess)}
          >
            {t('ipAllowList.tab')}
          </SubNavigationButton>
        )}
      </SubNavigationBar>

      {activeTab === TABS.IpAccess && companyId && (
        <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-p-4">
          <IpAllowListEditor companyId={companyId} />
        </div>
      )}

      {activeTab === TABS.Company && (
        <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-p-4">
          <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between">
            <span className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium">
              <AdminIcon
                name={getCompanyTypeIcon(company.type, company.accountingOnly)}
              />
              {t(getCompanyTypeName(company.type, company.accountingOnly))}
            </span>
            {isAdmin() && (
              <Button
                variant="primary"
                mode="outline"
                size="sm"
                disabled={isEmpty(company.nip)}
                onClick={fillFromGus}
              >
                {t('administrationPanel-companies.fill-from-gus')}
              </Button>
            )}
          </div>

          <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 xl:tw-grid-cols-4">
            <TextField
              fullWidth
              label={t('administrationPanel-companies.company-name')}
              value={company.companyName ?? ''}
              onChange={(event) => patch({ companyName: event.target.value })}
              error={error('companyName')}
            />
            <TextField
              fullWidth
              readOnly
              label={t('administrationPanel-companies.unique-identifier')}
              value={company.uniqueIdentifier ?? ''}
            />
            <TextField
              fullWidth
              label={t('administrationPanel-companies.nip')}
              value={company.nip ?? ''}
              onChange={(event) => patch({ nip: event.target.value })}
              error={error('nip')}
            />
            <TextField
              fullWidth
              label={t('administrationPanel-companies.license-number')}
              value={company.licenseNumber ?? ''}
              onChange={(event) => patch({ licenseNumber: event.target.value })}
            />

            <TextField
              fullWidth
              label={t('administrationPanel-companies.contact-phone')}
              value={company.contactPhone ?? ''}
              onChange={(event) => patch({ contactPhone: event.target.value })}
              error={error('contactPhone')}
            />
            <TextField
              fullWidth
              label={t('administrationPanel-companies.contact-email')}
              value={company.contactEmail ?? ''}
              onChange={(event) => patch({ contactEmail: event.target.value })}
              error={error('contactEmail')}
            />
            <TextField
              fullWidth
              label={t('administrationPanel-companies.contact-cc-email')}
              value={company.contactCCEmail ?? ''}
              onChange={(event) => patch({ contactCCEmail: event.target.value })}
              error={error('contactCCEmail')}
            />
            <TextField
              fullWidth
              label={t('administrationPanel-companies.street')}
              value={company.street ?? ''}
              onChange={(event) => patch({ street: event.target.value })}
            />

            <TextField
              fullWidth
              label={t('administrationPanel-companies.building-number')}
              value={company.buildingNumber ?? ''}
              onChange={(event) => patch({ buildingNumber: event.target.value })}
            />
            <TextField
              fullWidth
              label={t('administrationPanel-companies.house-number')}
              value={company.houseNumber ?? ''}
              onChange={(event) => patch({ houseNumber: event.target.value })}
            />
            <TextField
              fullWidth
              label={t('administrationPanel-companies.postal-code')}
              value={company.postalCode ?? ''}
              onChange={(event) => patch({ postalCode: event.target.value })}
            />
            <TextField
              fullWidth
              label={t('administrationPanel-companies.city')}
              value={company.city ?? ''}
              onChange={(event) => patch({ city: event.target.value })}
            />

            {!company.accountingOnly && (
              <TextField
                fullWidth
                label={t('administrationPanel-companies.email')}
                value={company.email ?? ''}
                onChange={(event) => patch({ email: event.target.value })}
                error={error('email')}
              />
            )}
            {!company.accountingOnly && (
              <TextField
                fullWidth
                label={t('administrationPanel-companies.notifications-email')}
                value={company.notificationsEmail ?? ''}
                onChange={(event) =>
                  patch({ notificationsEmail: event.target.value })
                }
                error={error('notificationsEmail')}
              />
            )}

            {isAdmin() && !company.accountingOnly && (
              <div className="tw-flex tw-flex-col tw-gap-1">
                <span className="tw-text-sm tw-font-medium tw-text-gray-700">
                  {t('administrationPanel-companies.remote-import')}
                </span>
                <Dropdown
                  compact
                  placeholder={t('administrationPanel-companies.remote-import')}
                  options={ImportTypes.map((item) => ({
                    value: String(item.key),
                    label: t(
                      `administrationPanel-companies.remote-import-${item.type}`,
                    ),
                  }))}
                  value={String(company.useLocalImport)}
                  onChange={(value) => patch({ useLocalImport: Number(value) })}
                />
              </div>
            )}

            <TextField
              fullWidth
              readOnly
              label={t('administrationPanel-companies.created-by')}
              value={company.createdBy ?? '-'}
            />
          </div>

          <div className="tw-mt-6 tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 xl:tw-grid-cols-4">
            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-gray-700">
                {t('administrationPanel-companies.created-date')}
              </span>
              <AdminDateField withTime value={company.createdDate} disabled onChange={() => {}} />
            </div>

            <div className="tw-flex tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-gray-700">
                {t('administrationPanel-companies.last-synchronization-date')}
              </span>
              {company.lastSynchronizationDate ? (
                <span className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
                  {company.synchronizationSource === 1 && (
                    <span className="tw-rounded tw-bg-[#7975FB] tw-px-1 tw-text-[11px] tw-font-medium tw-text-white">
                      U
                    </span>
                  )}
                  {moment.utc(company.lastSynchronizationDate).local().format(TIME_FORMAT)}
                  <span className="tw-italic tw-opacity-70">
                    {formatExtendedDuration(
                      duration(
                        moment().diff(moment(company.lastSynchronizationDate)),
                      ),
                      true,
                    )}
                  </span>
                </span>
              ) : (
                <span className="tw-text-sm">?</span>
              )}
            </div>

            {(isAdmin() || isPaymentAdmin()) && (
              <div className="tw-flex tw-flex-col tw-gap-1 md:tw-col-span-2">
                <span className="tw-text-sm tw-font-medium tw-text-gray-700">
                  {t('administrationPanel-companies.note')}
                </span>
                <textarea
                  className="tw-h-24 tw-w-full tw-resize-none tw-rounded-md tw-border tw-border-gray-300 tw-p-2 tw-text-sm"
                  style={{ background: noteTouched ? '#EEF1FA' : '#fff' }}
                  value={company.note ?? ''}
                  onChange={(event) => {
                    setNoteTouched(true)
                    patch({ note: event.target.value })
                  }}
                />
              </div>
            )}
          </div>

          {isAdmin() && company.accountingOnly && (
            <div className="tw-mt-6 tw-max-w-md tw-flex-col tw-gap-1">
              <span className="tw-text-sm tw-font-medium tw-text-gray-700">
                {t('audit-report.groups')}
              </span>
              <Dropdown
                multiple
                showSelectAll
                placeholder={t('administrationPanel-users.choose-group')}
                options={groups.map((group) => ({
                  value: String(group.id),
                  label: group.name ?? '',
                }))}
                value={selectedGroups}
                onChange={(values: string[]) => {
                  setSelectedGroups(values)
                  setCanSave(true)
                }}
              />
              {error('selectedGroups') && (
                <span className="tw-text-xs tw-text-red-500">
                  {error('selectedGroups')}
                </span>
              )}
            </div>
          )}

          {isAdmin() && company.accountingOnly && (
            <div className="tw-mt-6">
              <p className="tw-mb-2 tw-bg-base-200 tw-px-2 tw-py-1 tw-text-sm tw-font-medium">
                {t('administrationPanel-companies.modules')}
              </p>
              <div className="tw-flex tw-flex-col tw-gap-2">
                {company.tenant == null && (
                  <CheckboxField
                    id="isEwiSystem"
                    checked={company.isEwiSystem}
                    label={t('administrationPanel-companies.ewiSystemModule')}
                    onChange={() => patch({ isEwiSystem: !company.isEwiSystem })}
                  />
                )}
                <CheckboxField
                  id="timesheetModule"
                  checked={company.timesheetModuleEnabled}
                  label={t('administrationPanel-companies.timesheetModule')}
                  onChange={() =>
                    patch({
                      timesheetModuleEnabled: !company.timesheetModuleEnabled,
                    })
                  }
                />
                <CheckboxField
                  id="blockTimesheetCalculation"
                  checked={company.blockTimesheetCalculation}
                  label={t(
                    'administrationPanel-companies.blockTimesheetCalculation',
                  )}
                  onChange={() =>
                    patch({
                      blockTimesheetCalculation:
                        !company.blockTimesheetCalculation,
                    })
                  }
                />
              </div>
            </div>
          )}

          {isAdmin() && !company.accountingOnly && (
            <div className="tw-mt-6">
              <CheckboxField
                id="isServiceAccount"
                checked={isService}
                // Mirrors SERVICE_COMPANY_HAS_DATABASE on the server.
                disabled={
                  loaded?.type !== CompanyType.SERVICE && !!company.databaseName
                }
                label={t('administrationPanel-companies.is-service-account')}
                onChange={(event) =>
                  patch({
                    type: (event.target as HTMLInputElement).checked
                      ? CompanyType.SERVICE
                      : CompanyType.NORMAL,
                  })
                }
              />
              {loaded?.type !== CompanyType.SERVICE &&
                !!company.databaseName &&
                loaded?.tenantId == null &&
                loaded?.tenant == null && (
                  <div className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                    <Button
                      type="button"
                      variant="primary"
                      mode="outline"
                      size="sm"
                      // The new account copies the saved row, and the edits would be dropped.
                      disabled={canSave}
                      onClick={() => setConvertOpen(true)}
                    >
                      {t('serviceAccountSplit.open-convert')}
                    </Button>
                    <span className="tw-text-xs tw-text-gray-500">
                      {t(
                        canSave
                          ? 'serviceAccountSplit.open-convert-unsaved'
                          : 'serviceAccountSplit.open-convert-hint',
                      )}
                    </span>
                  </div>
                )}
            </div>
          )}
          {convertOpen && loaded && (
            <SplitServiceCompanyModal
              convert
              account={{ id: loaded.id, companyName: loaded.companyName ?? '' }}
              onClose={() => setConvertOpen(false)}
              onSplit={(newAccountId) => {
                setConvertOpen(false)
                navigate(
                  `${ROUTES.AdministrationPanel.NoCompanyTab.replace(':tab', 'serviceAccount')}?account=${newAccountId}`,
                )
              }}
            />
          )}

          {!company.accountingOnly && (
            <div className="tw-mt-6 tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-3">
              {isService && (
                <TextField
                  fullWidth
                  readOnly
                  label={t('administrationPanel-companies.servers')}
                  value={servers}
                />
              )}
              {showServerInfo && (
                <TextField
                  fullWidth
                  readOnly
                  label={t('administrationPanel-companies.server')}
                  value={company.serverName ?? ''}
                />
              )}
              {showServerInfo && (
                <TextField
                  fullWidth
                  readOnly
                  label={t('administrationPanel-companies.database')}
                  value={company.databaseName ?? ''}
                />
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === TABS.User && (
        <CompanyUsersTab
          companyId={companyId}
          serviceAccountId={loaded?.type === CompanyType.SERVICE ? companyId : null}
          users={users}
          availableUsers={availableUsers as UserModel[]}
          roles={roles as RolePermissionModel[]}
          onChange={(next) => {
            setUsers(next)
            setCanSave(true)
          }}
          onRemove={async (user) => {
            await removeUserFromCompany({
              userId: parseInt(user.id),
              companyId: company.id,
            })
            setUsers((prev) => prev.filter((item) => item.id !== user.id))
            setCanSave(true)
          }}
          onInvited={() => companyUsersQuery.refetch()}
        />
      )}

      {/* Lista IP zapisuje się osobno, własnym przyciskiem. */}
      {canSave && activeTab !== TABS.IpAccess && (
        <div className="tw-flex tw-flex-shrink-0 tw-justify-center tw-gap-2 tw-border-t tw-border-base-300 tw-py-2">
          <Button variant="primary" size="sm" onClick={save}>
            {t('operation.save')}
          </Button>
          <Button
            variant="primary"
            mode="outline"
            size="sm"
            onClick={() => navigate(backUrl)}
          >
            {t('operation.close')}
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * Ekran edycji firmy - funkcyjny odpowiednik `app/components/administration/editCompany.tsx`
 * (klasa na `ValidatorBase`, react-table v6, `FloatingLabelInput`). Zakładka płatności
 * odeszła wcześniej razem z przeniesieniem rozliczeń do panelu Claro.
 */
export const AdminEditCompanyPage: React.FC = () => (
  <TableProvider>
    <AdminEditCompanyPageContent />
  </TableProvider>
)
